import { inject, Injectable, InjectionToken } from '@angular/core';
import { IframeBridgeMessage, IframeBridgeService } from './iframe-bridge.service';

/**
 * A handler for one bridge key.
 * Receives the typed payload and the source window (for replies).
 */
export type IframeBridgeDialogHandler<T = unknown> = (msg: IframeBridgeMessage<T>) => Promise<void>;

/**
 * Multi-provider token for eager-loading bridge feature services.
 *
 * Each feature that handles iframe messages provides its service class under
 * this token so BridgeServicesInitializerService can force-construct them.
 *
 * In a module's providers:
 *   { provide: IFRAME_BRIDGE_REGISTRAR, useExisting: MyFeatureBridgeService, multi: true }
 *
 * The feature service registers its own handlers by injecting
 * IframeBridgeDialogService and calling register() in its constructor.
 */
export const IFRAME_BRIDGE_REGISTRAR = new InjectionToken<unknown[]>('IFRAME_BRIDGE_REGISTRAR');

/**
 * Central dispatcher for iframe → Angular dialog requests.
 *
 * Feature services call register() in their own constructors to add handlers.
 * This service has no knowledge of individual features — it is a pure bus.
 *
 * Adding a new dialog feature:
 *   1. Create a service that injects IframeBridgeDialogService and calls
 *      register() in its constructor.
 *   2. Provide it in a module:
 *        MyFeatureService,
 *        { provide: IFRAME_BRIDGE_REGISTRAR, useExisting: MyFeatureService, multi: true }
 *   3. No changes to AppComponent or IframeBridgeDialogService are needed.
 */
@Injectable()
export class IframeBridgeDialogService {
  private readonly _bridge = inject(IframeBridgeService);

  /**
   * Subscribe to one bridge key with a lazy async handler.
   * The handler is only invoked (and the dialog chunk downloaded) when the
   * matching postMessage arrives from the iframe.
   */
  public register<T>(key: string, handler: IframeBridgeDialogHandler<T>): void {
    this._bridge.on<T>(key).subscribe((msg) => handler(msg));
  }
}
